import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, User } from '@/components/ui/icons';
import { cn } from '@/lib/utils';
import { IconButton } from '@/components/ui/IconButton';

export type UserMenuProps = {
  profileLabel: string;
  logoutLabel: string;
  profileTo?: string;
  showLabel?: boolean;
  onLogout: () => void;
  logoutPending?: boolean;
  className?: string;
};

export function UserMenu({
  profileLabel,
  logoutLabel,
  profileTo = '/profile',
  showLabel = true,
  onLogout,
  logoutPending = false,
  className,
}: UserMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <IconButton
        type="button"
        variant="nav"
        size="sm"
        aria-label={profileLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
        className={showLabel ? 'lg:w-auto lg:gap-2 lg:px-3' : undefined}
      >
        <User className="h-5 w-5" aria-hidden />
        {showLabel ? <span className="hidden font-sans text-sm font-medium lg:inline">{profileLabel}</span> : null}
      </IconButton>
      {open ? (
        <div
          id={menuId}
          role="menu"
          className="absolute right-0 z-50 mt-2 min-w-[11rem] overflow-hidden rounded-md border border-brand-700/30 bg-brand-950 py-1 shadow-lg"
        >
          <Link
            role="menuitem"
            to={profileTo}
            className="flex items-center gap-2 px-3 py-2.5 font-sans text-sm text-brand-100 hover:bg-brand-900 hover:text-brand-400"
            onClick={() => setOpen(false)}
          >
            <User className="h-4 w-4" aria-hidden />
            {profileLabel}
          </Link>
          <button
            role="menuitem"
            type="button"
            disabled={logoutPending}
            className="flex w-full items-center gap-2 px-3 py-2.5 font-sans text-sm text-brand-100 hover:bg-brand-900 hover:text-red-300 disabled:opacity-50"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            <LogOut className="h-4 w-4" aria-hidden />
            {logoutLabel}
          </button>
        </div>
      ) : null}
    </div>
  );
}
